/**
 * Game Mode Selection component - allows players to choose between modes and AI difficulty
 */

import React, { useState } from 'react';
import { IGameModeSelectionProps, GameMode, AIDifficulty } from '../types';
import { EyeTrackableButton } from './eye-trackable-button';
import './game-mode-selection.scss';

export const GameModeSelection: React.FC<IGameModeSelectionProps> = ({
  onModeSelect,
  selectedMode,
}) => {
  const [showDifficultySelection, setShowDifficultySelection] = useState(false);

  const gameModes = [
    {
      mode: GameMode.PlayerVsPlayer,
      title: 'Player vs Player',
      description: 'Play with a friend locally',
      icon: '👥',
      color: '#74b9ff',
    },
    {
      mode: GameMode.PlayerVsComputer,
      title: 'Player vs Computer',
      description: 'Challenge the AI',
      icon: '🤖',
      color: '#fd79a8',
    },
  ];

  const difficultyLevels = [
    {
      difficulty: 'easy' as AIDifficulty,
      title: 'Easy',
      description: 'Relaxed gameplay, perfect for beginners',
      icon: '😊',
      color: '#00b894',
    },
    {
      difficulty: 'medium' as AIDifficulty,
      title: 'Medium',
      description: 'Balanced challenge for most players',
      icon: '🤔',
      color: '#fdcb6e',
    },
    {
      difficulty: 'hard' as AIDifficulty,
      title: 'Hard',
      description: 'Maximum challenge for experts',
      icon: '🤯',
      color: '#e84393',
    },
  ];

  const handleModeSelect = (mode: GameMode) => {
    if (mode === GameMode.PlayerVsComputer) {
      setShowDifficultySelection(true);
    } else {
      onModeSelect(mode);
    }
  };

  const handleDifficultySelect = (difficulty: AIDifficulty) => {
    onModeSelect(GameMode.PlayerVsComputer, difficulty);
  };

  const handleBackToModeSelection = () => {
    setShowDifficultySelection(false);
  };

  if (showDifficultySelection) {
    return (
      <div className='game-mode-selection' data-testid='difficulty-selection'>
        {/* Back button positioned outside the main container */}
        <div className='game-mode-selection__back-button-container'>
          <EyeTrackableButton
            className='game-mode-selection__mode eye-trackable-button--positioned'
            onClick={handleBackToModeSelection}
            variant='mode-selector'
            data-gaze-region='true'
            data-gaze-region-margin='2vh'            
            data-testid='back-to-mode-selection'
          >
            ← Back to Game Modes
          </EyeTrackableButton>
        </div>

        <div className='game-mode-selection__container'>
          <h2 className='game-mode-selection__title'>Choose Difficulty</h2>
          <p className='game-mode-selection__subtitle'>How challenging should the computer be?</p>

          <div className='game-mode-selection__modes'>
            {difficultyLevels.map(difficultyInfo => (
              <EyeTrackableButton
                key={difficultyInfo.difficulty}
                className='game-mode-selection__mode eye-trackable-button--positioned'
                onClick={() => handleDifficultySelect(difficultyInfo.difficulty)}
                variant='mode-selector'
                data-gaze-region='true'
                data-testid={`difficulty-${difficultyInfo.difficulty}`}
                style={{ borderColor: difficultyInfo.color }}
              >
                <div className='game-mode-selection__mode-icon'>{difficultyInfo.icon}</div>
                <div className='game-mode-selection__mode-content'>
                  <h3 className='game-mode-selection__mode-title'>{difficultyInfo.title}</h3>
                  <p className='game-mode-selection__mode-description'>{difficultyInfo.description}</p>
                </div>
              </EyeTrackableButton>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='game-mode-selection' data-testid='game-mode-selection'>
      <div className='game-mode-selection__container'>
        <h2 className='game-mode-selection__title'>Choose Game Mode</h2>
        <p className='game-mode-selection__subtitle'>Select how you want to play!</p>

        <div className='game-mode-selection__modes'>
          {gameModes.map(modeInfo => (
            <EyeTrackableButton
              key={modeInfo.mode}
              className={`game-mode-selection__mode eye-trackable-button--positioned ${
                selectedMode === modeInfo.mode ? 'game-mode-selection__mode--selected' : ''
              }`}
              onClick={() => handleModeSelect(modeInfo.mode)}
              variant='mode-selector'
              data-gaze-region='true'              
              data-testid={`mode-${modeInfo.mode}`}
              style={{ borderColor: modeInfo.color }}
            >
              <div className='game-mode-selection__mode-icon'>{modeInfo.icon}</div>
              <div className='game-mode-selection__mode-content'>
                <h3 className='game-mode-selection__mode-title'>{modeInfo.title}</h3>
                <p className='game-mode-selection__mode-description'>{modeInfo.description}</p>
              </div>

              {selectedMode === modeInfo.mode && (
                <div className='game-mode-selection__checkmark'>✓</div>
              )}
            </EyeTrackableButton>
          ))}
        </div>
      </div>
    </div>
  );
};
